import { rd } from '@passionware/monads';
import type { SimpleEvent } from '@passionware/simple-event';
import { TextSearch } from 'lucide-react';
import { useSimpleStore } from '@passionware/simple-store';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AnySpec } from 'v5/core/form-schema/formKinds.types';
import type { EntitySchema } from 'v5/core/form-schema/formSchema.types';
import type { ResourceKinds } from 'v5/core/entity-values/values.types';
import { Skeleton } from 'v5/design-system/Skeleton';
import type { DelegatedCoreField } from 'v5/core/entity-schema/resource/resolveResourceSchema';
import { createScopeSectionIconRenderer } from 'v5/features/core/enterprise/renderScopeSectionIcon';
import { renderError } from 'v5/patterns/feedback/ErrorAlert';
import {
  ExclusiveEditProvider,
  useCreateExclusiveEdit
} from 'v5/patterns/forms/exclusive-edit/ExclusiveEditContext';
import { FieldJumpDialog } from 'v5/patterns/forms/schema-form/FieldJumpDialog';
import type {
  FormKit,
  UnitBindings,
  UnitTarget
} from 'v5/patterns/forms/schema-form/schemaForm.types';
import { SchemaSections } from 'v5/patterns/forms/schema-form/SchemaSections';
import { useDiscardChangesPrompt } from 'v5/patterns/forms/unsaved-changes/useDiscardChangesPrompt';
import { useRegisterDirtyStateIfProtected } from 'v5/patterns/forms/unsaved-changes/useRegisterDirtyState';
import type { WithServices } from 'v5/platform/ts/services';
import type { WithPageCommandsService } from 'v5/services/front/PageCommandsService/PageCommandsService';
import type { WithUserPreferencesService } from 'v5/services/front/UserPreferencesService/UserPreferencesService';
import { fieldGroupFold } from './fieldGroupFold';
import { renderResourceUnitIcon } from './resourceUnitIcon';
import {
  ResourceFormServices,
  useResourceFormModel
} from './useResourceFormModel';

export interface ResourceDetailsFormProps {
  services: ResourceFormServices['services'] &
    /** Field groups folded away, remembered per user. */
    WithUserPreferencesService &
    /** "Jump to field" (`F`) while the form is shown. */
    WithPageCommandsService;
  resourceId: number;
  /** Merge modal / previews: everything read-only regardless of capability. */
  forceReadOnly?: boolean;
  /**
   * `false`: the Global section alone, without its header or section tabs —
   * for hosts that show no workspaces (the enterprise flag off, browsing the
   * organization).
   */
  workspaceSections?: boolean;
  /**
   * Opens the emitted unit for editing (`globalCoreUnitId`): shortcuts from
   * the host, such as double-clicking the name in the pane header, and the
   * form's own field finder.
   */
  editUnitSignal: SimpleEvent<UnitTarget>;
}

// The resource pane header (`ResourceGroupsEditor`) renders and edits groups,
// so the form leaves that unit out instead of duplicating it.
const PRESENTED_BY_HOST: ReadonlyArray<DelegatedCoreField> = ['groups'];

/**
 * Edit-mode resource form: Global section plus one collapsible section per
 * member workspace, each unit saving through `ResourceMutationService.apply`.
 * Fills its host (a bounded flex column) and scrolls itself below the
 * section tabs; the host sets the gutter (`SCHEMA_FORM_GUTTER`) instead of
 * padding it.
 * `F` (or the command bar) finds a field by its name or a value and opens
 * it. One unit at a time is in edit mode: opening another, collapsing its section
 * or Esc asks before unsaved changes are dropped. Leaving the form is the
 * host's call: it declares its unsaved draft to the surrounding
 * `UnsavedChangesProtection` (contact page, resource drawer), which knows
 * which navigation keeps the pane mounted.
 */
export function ResourceDetailsForm({
  resourceId,
  forceReadOnly = false,
  workspaceSections = true,
  editUnitSignal,
  services
}: ResourceDetailsFormProps) {
  const modelInput = useMemo(
    () => ({
      mode: 'edit' as const,
      resourceId,
      forceReadOnly,
      presentedByHost: PRESENTED_BY_HOST
    }),
    [resourceId, forceReadOnly]
  );
  const model = useResourceFormModel(modelInput, services);
  const scope = rd.tryGet(services.appScopeService.useResolvedAppScope());
  const discardPrompt = useDiscardChangesPrompt();
  const exclusiveEdit = useCreateExclusiveEdit(discardPrompt.confirmDiscard);
  useRegisterDirtyStateIfProtected(
    useSimpleStore(exclusiveEdit.hasUnsavedDraft)
  );

  return (
    <ExclusiveEditProvider value={exclusiveEdit}>
      {rd
        .journey(model)
        .wait(<Skeleton className="h-64 w-full" />)
        .catch(renderError)
        .map(model => {
          if (model.mode !== 'edit') return null;
          const schema = workspaceSections
            ? model.schema
            : {
                ...model.schema,
                sections: model.schema.sections.filter(
                  section => section.id === 'global'
                )
              };
          return (
            <>
              <SchemaSections
                // Another resource starts with no unit in edit mode.
                key={resourceId}
                schema={schema}
                kit={model.kit}
                bindings={model.bindings}
                variant="plain"
                size="sm"
                navigation="tabs"
                scroll="own"
                singleSectionHeader={workspaceSections}
                editUnitSignal={editUnitSignal}
                unitFold={
                  scope === undefined || model.resource.resource_type_id == null
                    ? undefined
                    : fieldGroupFold(
                        services.userPreferencesService,
                        scope,
                        model.resource.resource_type_id
                      )
                }
                renderSectionIcon={createScopeSectionIconRenderer(
                  model.workspaceIcons
                )}
                renderUnitIcon={renderResourceUnitIcon}
              />
              {/* A read-only preview (the merge dialog) is no page to jump around. */}
              {!forceReadOnly && (
                <ResourceFieldJump
                  schema={schema}
                  kit={model.kit}
                  bindings={model.bindings}
                  onPick={target => editUnitSignal.emit(target)}
                  services={services}
                />
              )}
            </>
          );
        })}
      {discardPrompt.dialog}
    </ExclusiveEditProvider>
  );
}

/** The field finder over the form, offered as the page's `F` command. */
function ResourceFieldJump({
  schema,
  kit,
  bindings,
  onPick,
  services
}: {
  schema: EntitySchema<AnySpec<ResourceKinds>>;
  kit: FormKit<ResourceKinds>;
  bindings: UnitBindings<ResourceKinds>;
  onPick(target: UnitTarget): void;
} & WithServices<[WithPageCommandsService]>) {
  const { t } = useTranslation('patterns', { keyPrefix: 'SchemaForm.jump' });
  const [open, setOpen] = useState(false);
  services.pageCommandsService.useOffer({
    id: 'jump-to-field',
    label: t('title'),
    icon: TextSearch,
    hotkey: 'f',
    run: () => setOpen(true)
  });
  return (
    <FieldJumpDialog
      open={open}
      onOpenChange={setOpen}
      schema={schema}
      kit={kit}
      bindings={bindings}
      onPick={onPick}
    />
  );
}
